"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { Separator } from "@/components/ui/separator";
import {
  getIconForListingStatus,
  getThemeColorForListingStatus,
} from "@/lib/listing-utils";
import type { AnyDetailedListing } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { Fragment, useCallback } from "react";

export type BaseListingStatusPickerProps<
  T extends AnyDetailedListing["status"],
> = {
  types: T[];
};

export type SingleListingStatusPickerProps<
  T extends AnyDetailedListing["status"],
> = {
  type: "single";
  onSelect?: (status: T) => void;
  selected?: T;
};

export type MultipleListingStatusPickerProps<
  T extends AnyDetailedListing["status"],
> = {
  type: "multiple";
  onSelect?: (status: T[]) => void;
  selected?: T[];
};

export type ListingStatusPickerProps<T extends AnyDetailedListing["status"]> =
  BaseListingStatusPickerProps<T> &
    (SingleListingStatusPickerProps<T> | MultipleListingStatusPickerProps<T>);

export function ListingStatusPicker<T extends AnyDetailedListing["status"]>({
  types,
  type,
  onSelect,
  selected,
}: ListingStatusPickerProps<T>) {
  const t = useTranslations();

  const handleSelect = useCallback(
    (status: T) => {
      if (type === "single") {
        onSelect?.(status);
      } else if (type === "multiple") {
        if (selected?.includes(status)) {
          onSelect?.(selected.filter((s) => s !== status));
        } else {
          onSelect?.(Array.from(new Set([...(selected || []), status])));
        }
      }
    },
    [onSelect, selected, type],
  );

  return (
    <div className="max-h-full md:max-h-[600px] overflow-y-auto">
      <div className="flex flex-col">
        {types.map((status, idx) => {
          const color = getThemeColorForListingStatus(status);
          const icon = getIconForListingStatus(status);

          return (
            <Fragment key={status}>
              <div
                className="px-6 py-3 flex items-center gap-6 cursor-pointer"
                onClick={() => handleSelect(status)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSelect(status);
                  }
                }}
              >
                <Icon
                  icon={icon}
                  className={cn("shrink-0", {
                    "text-theme-green": color === "green",
                    "text-theme-purple": color === "purple",
                    "text-theme-blue": color === "blue",
                    "text-theme-orange": color === "orange",
                    "text-theme-red": color === "red",
                    "text-black": color === "black",
                  })}
                />

                <div className="flex-1 flex flex-col items-start text-left gap-1">
                  <div className="text-sm font-medium">
                    {t(`saas.enums.listingStatus.${status}` as any)}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {t(
                      `saas.enums._meta.listingStatusDescription.${status}` as any,
                    )}
                  </div>
                </div>

                {type === "single" && selected === status && (
                  <Checkbox
                    checked
                    className="w-5 h-5 border-2 rounded-[6px] shrink-0"
                  />
                )}

                {type === "multiple" && (
                  <Checkbox
                    checked={selected?.includes(status)}
                    className="w-5 h-5 border-2 rounded-[6px] shrink-0"
                  />
                )}
              </div>

              {idx < types.length - 1 && <Separator />}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
